import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, orderBy, updateDoc, writeBatch } from 'firebase/firestore';
import { IoClose, IoAdd, IoTrash, IoList, IoWarningOutline, IoCheckmarkCircleOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import './PlanCuentasModal.css';

// Default Seed Data for MX/General Accounting
import { SAT_CONDO_TEMPLATE } from '../utils/satTemplate';

function PlanCuentasModal({ onClose }) {
    const { userData } = useAuth();
    const [cuentas, setCuentas] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form State
    const [newDesc, setNewDesc] = useState('');
    const [newNum, setNewNum] = useState('');
    const [newType, setNewType] = useState('GASTO'); // GASTO | INGRESO | ACTIVO | PASIVO
    const [adding, setAdding] = useState(false);

    // Dialog & Notification States
    const [deleteTargetCount, setDeleteTargetCount] = useState(null);
    const [toDeleteIds, setToDeleteIds] = useState([]);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [infoMessage, setInfoMessage] = useState(null);

    useEffect(() => {
        fetchCuentas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCuentas = async () => {
        if (!userData?.condominioId) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, "cuentas_contables"),
                where("condominioId", "==", userData.condominioId),
                orderBy("numero")
            );
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(doc => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setCuentas(list);
        } catch (e) {
            console.error("Error fetching accounts:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!newNum || !newDesc) {
            setErrorMessage("Por favor ingresa número y descripción de la cuenta.");
            return;
        }
        setAdding(true);
        try {
            const exists = cuentas.some(c => c.numero === newNum);
            if (exists) {
                setErrorMessage(`El número de cuenta ${newNum} ya existe.`);
                setAdding(false);
                return;
            }

            await addDoc(collection(db, "cuentas_contables"), {
                condominioId: userData.condominioId,
                numero: newNum,
                nombre: newDesc,
                tipo: newType,
                naturaleza: ['GASTO', 'ACTIVO'].includes(newType) ? 'DEUDORA' : 'ACREEDORA'
            });

            setNewNum('');
            setNewDesc('');
            setSuccessMessage("Cuenta contable agregada exitosamente.");
            fetchCuentas();
        } catch (e) {
            console.error("Error adding account:", e);
            setErrorMessage("Error al agregar la cuenta.");
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta cuenta contable? Asegúrate de que no tenga movimientos o pólizas registradas.")) {
            try {
                await deleteDoc(doc(db, "cuentas_contables", id));
                setSuccessMessage("Cuenta contable eliminada correctamente.");
                fetchCuentas();
            } catch (e) {
                console.error("Error deleting account:", e);
                setErrorMessage("Error al eliminar la cuenta contable.");
            }
        }
    };

    const handleToggleEfectivo = async (id, currentVal) => {
        try {
            await updateDoc(doc(db, "cuentas_contables", id), {
                esCuentaEfectivo: !currentVal
            });
            fetchCuentas();
        } catch (e) {
            console.error("Error toggling cash account:", e);
        }
    };

    const handleSelectClasificacion = async (id, value) => {
        try {
            await updateDoc(doc(db, "cuentas_contables", id), {
                clasificacionFlujo: value === 'default' ? null : value
            });
            fetchCuentas();
        } catch (e) {
            console.error("Error updating classification:", e);
        }
    };

    const handleCleanDuplicates = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, "cuentas_contables"),
                where("condominioId", "==", userData.condominioId)
            );
            const snap = await getDocs(q);
            const seen = new Set();
            const toDelete = [];

            snap.docs.forEach(docSnap => {
                const data = docSnap.data();
                const num = data.numero;
                if (seen.has(num)) {
                    toDelete.push(docSnap.id);
                } else {
                    seen.add(num);
                }
            });

            if (toDelete.length === 0) {
                setInfoMessage("No se encontraron cuentas duplicadas.");
                setLoading(false);
                return;
            }

            setToDeleteIds(toDelete);
            setDeleteTargetCount(toDelete.length);
        } catch (e) {
            console.error("Error searching duplicates:", e);
            setErrorMessage("Error al buscar cuentas duplicadas.");
            setLoading(false);
        }
    };

    const executeCleanDuplicates = async () => {
        const ids = toDeleteIds;
        setDeleteTargetCount(null);
        setToDeleteIds([]);

        try {
            await Promise.all(ids.map(id => deleteDoc(doc(db, "cuentas_contables", id))));
            setSuccessMessage("Limpieza completada exitosamente.");
            fetchCuentas();
        } catch (e) {
            console.error("Error deleting duplicates:", e);
            setErrorMessage("Error al eliminar las cuentas duplicadas.");
            setLoading(false);
        }
    };

    const executeResetToTemplate = async () => {
        setShowResetConfirm(false);
        setLoading(true);
        try {
            // 1. Fetch and Delete all existing accounts for this condominium
            const q = query(
                collection(db, "cuentas_contables"),
                where("condominioId", "==", userData.condominioId)
            );
            const snap = await getDocs(q);
            const deletePromises = snap.docs.map(d => deleteDoc(d.ref));
            await Promise.all(deletePromises);

            // 2. Add all accounts from SAT_CONDO_TEMPLATE using batch
            const batch = writeBatch(db);
            SAT_CONDO_TEMPLATE.forEach(acc => {
                const newRef = doc(collection(db, "cuentas_contables"));
                batch.set(newRef, {
                    condominioId: userData.condominioId,
                    codigo: acc.numero,
                    nombre: acc.nombre,
                    tipo: acc.tipo,
                    numero: acc.numero,
                    descripcion: acc.nombre,
                    naturaleza: acc.naturaleza,
                    fechaCreacion: new Date().toISOString()
                });
            });
            await batch.commit();

            setSuccessMessage("Catálogo contable restablecido exitosamente a la plantilla SAT homologada de Kondify.");
            fetchCuentas();
        } catch (e) {
            console.error("Error resetting accounts:", e);
            setErrorMessage("Error al restablecer el catálogo contable.");
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 999 }}>
            {showResetConfirm && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#dc2626" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            ¿Restablecer Catálogo?
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Esta acción <strong>eliminará todas las cuentas actuales</strong> de este condominio y las reemplazará con la nueva plantilla homologada del SAT de Kondify. Esta acción no se puede deshacer. ¿Deseas continuar?
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={() => setShowResetConfirm(false)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={executeResetToTemplate}
                            >
                                Restablecer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteTargetCount !== null && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            ¿Eliminar Duplicados?
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Se encontraron <strong>{deleteTargetCount}</strong> cuentas duplicadas en el catálogo. ¿Deseas eliminarlas y dejar solo una de cada tipo?
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={() => {
                                    setDeleteTargetCount(null);
                                    setLoading(false);
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={executeCleanDuplicates}
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoCheckmarkCircleOutline size={60} color="#10b981" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Operación Exitosa
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {successMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setSuccessMessage(null)}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {infoMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoInformationCircleOutline size={60} color="#3b82f6" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Catálogo Limpio
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {infoMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setInfoMessage(null)}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#ef4444" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Aviso
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {errorMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setErrorMessage(null)}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            <div className="modal-content" style={{ maxWidth: '850px', width: '95%', height: '80vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header">
                    <h3><IoList /> Plan de Cuentas (Chart of Accounts)</h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => setShowResetConfirm(true)} className="secondary-btn small" style={{ borderColor: '#dc2626', color: '#dc2626' }} title="Restablecer catálogo a plantilla SAT de Kondify">
                            Restablecer Plantilla SAT
                        </button>
                        <button onClick={handleCleanDuplicates} className="secondary-btn small" title="Eliminar cuentas duplicadas">
                            Limpiar Duplicados
                        </button>
                        <button onClick={onClose} className="close-btn"><IoClose /></button>
                    </div>
                </div>

                <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                    <div className="add-account-section">
                        <h4>Agregar Nueva Cuenta</h4>
                        <div className="form-inline">
                            <input 
                                type="text" 
                                placeholder="Número (Ej. 601.52)" 
                                value={newNum} 
                                onChange={e => setNewNum(e.target.value)} 
                            />
                            <input 
                                type="text" 
                                placeholder="Nombre de la Cuenta" 
                                value={newDesc} 
                                onChange={e => setNewDesc(e.target.value)} 
                            />
                            <select value={newType} onChange={e => setNewType(e.target.value)}>
                                <option value="GASTO">GASTO</option>
                                <option value="INGRESO">INGRESO</option>
                                <option value="ACTIVO">ACTIVO</option>
                                <option value="PASIVO">PASIVO</option>
                            </select>
                            <button className="primary-btn small" onClick={handleAdd} disabled={adding}>
                                <IoAdd /> Agregar
                            </button>
                        </div>
                    </div>

                    {/* Check if seeded */}
                    {loading ? <p>Cargando catálogo...</p> : (
                        <table className="accounts-table">
                            <thead>
                                <tr>
                                    <th>Número</th>
                                    <th>Nombre</th>
                                    <th>Tipo</th>
                                    <th style={{ textAlign: 'center' }}>Efectivo</th>
                                    <th style={{ textAlign: 'left' }}>Clasificación Flujo</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cuentas.map(acc => (
                                    <tr key={acc.id} className={`row-${acc.tipo.toLowerCase()}`}>
                                        <td className="font-mono">{acc.numero}</td>
                                        <td>{acc.nombre}</td>
                                        <td><span className={`badge badge-${acc.tipo.toLowerCase()}`}>{acc.tipo}</span></td>
                                        <td style={{ textAlign: 'center' }}>
                                            {acc.tipo === 'ACTIVO' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={!!acc.esCuentaEfectivo}
                                                    onChange={() => handleToggleEfectivo(acc.id, acc.esCuentaEfectivo)}
                                                    style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                                    title="Marcar como cuenta de Caja/Bancos para el reporte de Flujo de Efectivo"
                                                />
                                            ) : '-'}
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            {!(acc.esCuentaEfectivo || acc.numero.startsWith('101') || acc.numero.startsWith('102')) ? (
                                                <select
                                                    value={acc.clasificacionFlujo || 'default'}
                                                    onChange={e => handleSelectClasificacion(acc.id, e.target.value)}
                                                    style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem', width: '130px', outline: 'none' }}
                                                >
                                                    <option value="default">Sistema (Por Tipo)</option>
                                                    <option value="operacion">Operación</option>
                                                    <option value="inversion">Inversión</option>
                                                    <option value="financiamiento">Financiamiento</option>
                                                    <option value="excluir">Excluir del Flujo</option>
                                                </select>
                                            ) : (
                                                <span style={{ color: '#9ca3af', fontSize: '0.8rem', fontStyle: 'italic' }}>Efectivo</span>
                                            )}
                                        </td>
                                        <td>
                                            <button className="icon-btn danger" onClick={() => handleDelete(acc.id)}>
                                                <IoTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PlanCuentasModal;
